// ABA "INVALIDOS" da Gestao de Vendas — pedido dele em 15/10/2026: *"ao lado da aba Contatos, vamos
// criar a aba Invalidos, com o status de cada contato"*. Ate ali o contato invalido so' se via linha
// a linha na aba Contatos (a lixeira no lugar do balao), sem filtro nenhum — e os grupos, que a aba
// Contatos esconde, nao apareciam em lugar algum.
//
// O STATUS e' o ponto da aba: cada um pede um gesto diferente. Grupo e lista se APAGAM; numero
// colado duas vezes se CONSERTA (a tela ja mostra a metade, que e' o numero); dois numeros colados
// pedem que alguem escolha qual; recusado pelo WhatsApp pede outro numero ou apagar.
//
// ⛔ As duas acoes sao as MESMAS pecas da aba Contatos, nunca copias: o lapis grava pelo
// `PUT /contatos/:id` (que confere o numero no WhatsApp e troca o endereco junto) e a lixeira e' o
// `excluirContatoDeVez` (previa do servidor + confirmacao). Nada aqui grava sem clique.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatTelefoneBR } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { Avatar } from '../../components/Avatar';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { atendimentoApi } from '../atendimento/lib/api';
import { useEtiquetas, type Etiqueta } from '../atendimento/lib/etiquetas-context';
import { BadgeEtiqueta } from '../atendimento/componentes/BadgeEtiqueta';
import { SeletorEtiquetas } from '../atendimento/componentes/SeletorEtiquetas';
import { excluirContatoDeVez } from './excluir-contato';

type Status = 'grupo' | 'lista' | 'repetido' | 'tamanho' | 'recusado' | 'endereco';

interface LinhaInvalida {
  id: number;
  nome: string | null;
  sobrenome: string | null;
  push_name: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
  criado_em: string;
  mensagens: number;
  recusado_em: string | null;
  /** TODAS as deste contato, ocultas inclusive — o `PUT` de etiquetas substitui a lista inteira. */
  etiquetas: Etiqueta[];
  status: Status;
  /** So' no `repetido`: a metade do numero, que e' o numero de verdade. */
  sugestao: string | null;
}

/** O que cada status quer dizer e o que fazer com ele — o texto que o operador le na linha. */
const ROTULO: Record<Status, { titulo: string; plural: string; acao: string }> = {
  repetido: { titulo: 'Número repetido duas vezes', plural: 'com número repetido', acao: 'Conserte pelo lápis: o número certo está ao lado.' },
  tamanho: { titulo: 'Número com tamanho errado', plural: 'com tamanho errado', acao: 'Parecem dois números colados (ou número de fora do Brasil) — escolha o certo pelo lápis, ou apague.' },
  recusado: { titulo: 'WhatsApp recusou o número', plural: 'recusados pelo WhatsApp', acao: 'O número não tem WhatsApp — troque pelo lápis, ou apague.' },
  grupo: { titulo: 'Grupo do WhatsApp', plural: 'grupos', acao: 'Grupo não recebe fly.' },
  lista: { titulo: 'Lista de transmissão ou status', plural: 'listas ou status', acao: 'É o que outra pessoa publicou, não uma conversa — não recebe fly.' },
  endereco: { titulo: 'Endereço que não é de WhatsApp', plural: 'outros endereços', acao: 'Não recebe mensagem.' },
};

/**
 * ⛔ Sugestao SO' quando a metade e' numero BRASILEIRO (55 + DDD + 8 ou 9 digitos). Medido no ar
 * em 15/10/2026: a Gabriela tem `14702407234` repetido — um numero dos EUA (+1 470) —, e a tela o
 * formatava como "(14) 70240-7234"; salvar isso faria o servidor por o 55 na frente e gravar um
 * numero que nao existe. Fora do padrao BR, a linha vira "tamanho errado", sem sugestao.
 */
function normalizar(l: LinhaInvalida): LinhaInvalida {
  if (l.status !== 'repetido' || !l.sugestao) return l;
  const br = l.sugestao.startsWith('55') && (l.sugestao.length === 12 || l.sugestao.length === 13);
  return br ? l : { ...l, status: 'tamanho', sugestao: null };
}

/**
 * A lixeira so' aparece onde ela pode funcionar. ⚠️ Medido no ensaio de 15/10/2026: os grupos de
 * verdade da loja (AMIGOS, 6.591 mensagens) e as listas/status de outras pessoas TEM mensagens, e o
 * servidor nao apaga contato com conversa (regra dele de 24/09/2026) — lixeira ali seria um beco
 * que sempre responde "nao da". No RECUSADO ela fica sempre, como na aba Contatos: mensagem de fly
 * que nunca chegou nao conta, e quem decide e' a previa do servidor.
 */
function podeApagar(l: LinhaInvalida): boolean {
  return l.status === 'recusado' || l.mensagens === 0;
}

// ⚠️ Etiqueta de "nao cliente" so' entra no seletor com a caixa "Na Gestao" marcada no Gerenciar
// ([DA-814] p.7, decisao dele em 15/10/2026 — antes era um nome fixo no codigo).

/** A ORDEM da lista: primeiro o que tem conserto, depois o que so' se apaga. */
const ORDEM: Status[] = ['repetido', 'tamanho', 'recusado', 'grupo', 'lista', 'endereco'];

/** Azul-marinho da grade — cor FIXA, a que ele pediu; o mesmo navy da barra de total do orcamento. */
const NAVY = '#1e3a8a';

function temNaoCliente(etqs: Etiqueta[]): boolean {
  return etqs.some((e) => !!e.nao_cliente);
}

function nomeDe(l: LinhaInvalida): string {
  return [l.nome, l.sobrenome].filter(Boolean).join(' ').trim() || l.push_name || 'sem nome';
}

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

export function ContatosInvalidos({ sinalRecarga }: { sinalRecarga: number }) {
  const toast = useToast();
  const dialog = useDialog();
  const [linhas, setLinhas] = useState<LinhaInvalida[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Edicao na propria linha, como na aba Contatos. Guarda o rascunho junto do id.
  const [editando, setEditando] = useState<{ id: number; nome: string; telefone: string; erro: string | null } | null>(null);
  const [gravando, setGravando] = useState(false);
  // Etiquetas: pedido dele no mesmo dia (*"habilite o recurso de mudar a Etiqueta do contato"*),
  // com o retangulo na coluna. Mesma forma da aba Contatos: a COLUNA e' o botao e o seletor abre
  // embaixo da linha.
  const [etqAberta, setEtqAberta] = useState<number | null>(null);
  const [gravandoEtq, setGravandoEtq] = useState<number | null>(null);
  const { visiveis: etiquetasVisiveis, atualizar: atualizarEtiquetas } = useEtiquetas();
  // ⛔ O provider de etiquetas nasce VAZIO e so' se enche onde alguem o carrega — sem isto o
  // seletor abriria sem opcao nenhuma pra quem entrou direto nesta aba (mesmo cuidado da aba Contatos).
  useEffect(() => {
    atendimentoApi.listarEtiquetas().then(atualizarEtiquetas).catch(() => {});
  }, [atualizarEtiquetas]);

  async function alternarEtiqueta(l: LinhaInvalida, etiquetaId: number) {
    const tem = l.etiquetas.some((e) => e.id === etiquetaId);
    // A lista que vai pro servidor parte de TODAS as do contato (ocultas inclusive): o `PUT`
    // substitui o conjunto, e mandar so' as visiveis apagaria a etiqueta de vendedora.
    const nova = tem
      ? l.etiquetas.filter((e) => e.id !== etiquetaId)
      : [...l.etiquetas, etiquetasVisiveis.find((e) => e.id === etiquetaId)!].filter(Boolean);
    setGravandoEtq(l.id);
    try {
      await atendimentoApi.atualizarEtiquetasContato(l.id, nova.map((e) => e.id));
      // Virou "nao cliente": a linha SAI na hora (e o seletor fecha junto) — sumir e' o efeito
      // pedido, e deixa-la na tela ate o F5 diria que nao funcionou (mesmo cuidado da aba Contatos).
      if (temNaoCliente(nova)) {
        setLinhas((ls) => (ls ?? []).filter((x) => x.id !== l.id));
        setEtqAberta(null);
        toast.sucesso(`"${nomeDe(l)}" marcado como não cliente — saiu da lista`);
      } else {
        setLinhas((ls) => (ls ?? []).map((x) => (x.id === l.id ? { ...x, etiquetas: nova } : x)));
      }
    } catch (e) {
      toast.erro((e as Error)?.message || 'Erro ao atualizar etiquetas');
    } finally {
      setGravandoEtq(null);
    }
  }

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<{ linhas: LinhaInvalida[] }>('/api/fila-retomada/contatos-invalidos');
      // ⛔ Contato com etiqueta de "nao cliente" (NAO CLIENTES, FORNECEDOR, ...) NAO entra — pedido
      // dele em 15/10/2026: *"quando marcar NAO CLIENTE, ela deve sumir da lista"*. E' a mesma regra
      // do resto da Gestao de Vendas ([DA-693]): quem carrega a etiqueta some de abas e contadores.
      setLinhas(r.linhas.filter((l) => !temNaoCliente(l.etiquetas)).map(normalizar));
      setErro(null);
    } catch (e) {
      setErro((e as Error)?.message || 'Falha ao carregar');
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar, sinalRecarga]);

  async function gravar(l: LinhaInvalida) {
    if (!editando) return;
    const digitos = editando.telefone.replace(/\D/g, '');
    if (!editando.nome.trim()) {
      setEditando({ ...editando, erro: 'O nome não pode ficar vazio.' });
      return;
    }
    if (digitos.length < 10 || digitos.length > 13) {
      setEditando({ ...editando, erro: 'Telefone com DDD: 10 ou 11 números, como (64) 99999-9999.' });
      return;
    }
    setGravando(true);
    try {
      // Mesmo `PUT` do lapis da aba Contatos: o servidor confere o numero no WhatsApp e troca o
      // endereco junto; recusa volta com o motivo e nada e' gravado.
      await atendimentoApi.atualizarContato(l.id, { nome: editando.nome.trim(), telefone: digitos });
      setEditando(null);
      toast.sucesso('Número trocado — conferido no WhatsApp');
      // Consertado, ele deixa de ser invalido e sai da lista. Recarrega em vez de tirar a linha
      // na mao: quem decide se ainda e' invalido e' a regra do servidor, nao a tela.
      await carregar();
    } catch (e) {
      setEditando((a) => (a && a.id === l.id ? { ...a, erro: (e as Error)?.message || 'Erro ao gravar' } : a));
    } finally {
      setGravando(false);
    }
  }

  async function apagar(l: LinhaInvalida) {
    if (!(await excluirContatoDeVez(l.id, dialog, toast))) return;
    setLinhas((ls) => (ls ?? []).filter((x) => x.id !== l.id));
  }

  if (erro) {
    return <div className="p-4 text-sm font-semibold rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>{erro}</div>;
  }
  if (!linhas) {
    return <div className="p-6 text-center" style={{ color: 'var(--text-muted)' }}><Loader2 size={18} className="animate-spin inline" /> Carregando...</div>;
  }

  const ordenadas = [...linhas].sort((a, b) => ORDEM.indexOf(a.status) - ORDEM.indexOf(b.status) || nomeDe(a).localeCompare(nomeDe(b), 'pt-BR'));
  const porStatus = ORDEM.map((s) => ({ s, n: linhas.filter((l) => l.status === s).length })).filter((x) => x.n > 0);

  return (
    <div>
      <div className="mb-3 text-sm" style={{ color: 'var(--panel-fg)' }}>
        <strong>{linhas.length}</strong> {linhas.length === 1 ? 'contato inválido' : 'contatos inválidos'}
        {porStatus.length > 0 && (
          <span style={{ color: 'var(--text-muted)' }}>
            {' · '}{porStatus.map((x) => `${x.n} ${ROTULO[x.s].plural}`).join(' · ')}
          </span>
        )}
      </div>

      {linhas.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nenhum contato inválido no Atendimento.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* Grade HORIZONTAL azul-marinho entre os registros (pedido dele com print, 15/10/2026).
              `collapse` e' o que deixa a borda do <tr> aparecer; `#1e3a8a` e' o navy que a casa
              ja usa na barra de total. So' horizontal: ele pediu pra separar os REGISTROS. */}
          <table className="tabela-grid" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${NAVY}` }}>
                <th style={{ textAlign: 'left' }}>Contato</th>
                <th style={{ textAlign: 'left' }}>Número gravado</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th style={{ textAlign: 'left' }}>Etiquetas</th>
                <th style={{ textAlign: 'left' }}>Desde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ordenadas.map((l) => {
                const r = ROTULO[l.status];
                const emEdicao = editando?.id === l.id;
                return (
                  <Fragment key={l.id}>
                  {/* Com o seletor aberto a linha de baixo e' a faixa dele, e a grade passa pra ela —
                      senao a linha separaria o registro da propria lista de etiquetas. */}
                  <tr style={etqAberta === l.id ? undefined : { borderBottom: `1px solid ${NAVY}` }}>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar nome={nomeDe(l)} fotoUrl={l.foto_url} tamanho={26} />
                        {nomeDe(l)}
                      </span>
                    </td>
                    {/* O numero aparece CRU, do endereco: mascarado, esconderia o defeito. */}
                    <td className="tabular-nums" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                      {l.jid.split('@')[0]}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div style={{ fontWeight: 700 }}>
                        {r.titulo}
                        {l.status === 'recusado' && l.recusado_em && <span style={{ fontWeight: 400 }}> em {dataCurta(l.recusado_em)}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {r.acao}
                        {!podeApagar(l) && <> Tem {l.mensagens} {l.mensagens === 1 ? 'mensagem' : 'mensagens'} — fica.</>}
                        {podeApagar(l) && (l.status === 'grupo' || l.status === 'lista' || l.status === 'endereco') && <> Sem mensagens — pode apagar.</>}
                        {l.sugestao && <> Número certo: <strong className="tabular-nums" style={{ color: 'var(--panel-fg)' }}>{formatTelefoneBR(l.sugestao)}</strong></>}
                      </div>
                      {emEdicao && editando && (
                        <div className="mt-2 flex items-end gap-2 flex-wrap">
                          <div style={{ width: 200 }}>
                            <label className="text-[11px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>Nome</label>
                            <input
                              autoFocus
                              value={editando.nome}
                              onChange={(e) => setEditando({ ...editando, nome: e.target.value, erro: null })}
                              onKeyDown={(e) => { if (e.key === 'Enter') void gravar(l); if (e.key === 'Escape') setEditando(null); }}
                              className="no-upper w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
                            />
                          </div>
                          <div style={{ width: 200 }}>
                            <label className="text-[11px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>Telefone (WhatsApp)</label>
                            <input
                              value={editando.telefone}
                              inputMode="tel"
                              onChange={(e) => setEditando({ ...editando, telefone: e.target.value, erro: null })}
                              onKeyDown={(e) => { if (e.key === 'Enter') void gravar(l); if (e.key === 'Escape') setEditando(null); }}
                              className="w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400 tabular-nums"
                              placeholder="Digite com DDD"
                            />
                          </div>
                          {/* Os botoes ficam numa linha PROPRIA, embaixo dos campos, Salvar a esquerda do
                              Cancelar (pedido dele com print, 15/10/2026). */}
                          <div className="w-full flex items-center gap-2">
                            <button type="button" onClick={() => void gravar(l)} disabled={gravando} className="btn-primario text-sm">
                              {gravando ? 'Gravando...' : 'Salvar'}
                            </button>
                            <button type="button" onClick={() => setEditando(null)} className="btn-secundario text-sm">Cancelar</button>
                          </div>
                          {editando.erro && (
                            <div className="w-full text-sm font-semibold rounded px-2 py-1" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                              {editando.erro}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {/* A COLUNA e' o botao, como na aba Contatos ([DA-705] p.9). */}
                      <button
                        type="button"
                        onClick={() => setEtqAberta(etqAberta === l.id ? null : l.id)}
                        title="Mudar as etiquetas deste contato"
                        className="flex flex-wrap gap-1 items-center text-left rounded px-1 py-0.5 hover:bg-black/5"
                        style={{ minHeight: 24, color: 'var(--panel-fg)' }}
                      >
                        {l.etiquetas.filter((e) => !e.ocultar).length === 0
                          ? <span style={{ color: 'var(--text-muted)' }}>sem etiqueta</span>
                          : l.etiquetas.filter((e) => !e.ocultar).map((e) => (
                            <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />
                          ))}
                      </button>
                    </td>
                    <td style={{ fontSize: 12 }}>{dataCurta(l.criado_em)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {/* Grupo e lista nao tem numero a consertar — so' a lixeira. */}
                      {l.status !== 'grupo' && l.status !== 'lista' && l.status !== 'endereco' && (
                        <button
                          type="button"
                          className="btn-icon"
                          title="Editar nome e telefone"
                          onClick={() => setEditando(emEdicao ? null : {
                            id: l.id,
                            nome: nomeDe(l),
                            // O campo ja nasce com o numero certo quando da' pra reconstruir: o
                            // gesto vira conferir e salvar. Nos outros casos, o numero cru.
                            telefone: l.sugestao ? formatTelefoneBR(l.sugestao) : l.jid.split('@')[0] ?? '',
                            erro: null,
                          })}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {podeApagar(l) && (
                        <button
                          type="button"
                          className="btn-icon"
                          style={{ color: '#dc2626' }}
                          title="Apagar este contato de vez"
                          aria-label={`Apagar o contato ${nomeDe(l)}`}
                          onClick={() => void apagar(l)}
                        >
                          <Trash2 size={17} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* O seletor abre numa FAIXA da largura inteira, embaixo da linha, em varias
                      colunas — o mesmo formato da aba Contatos (pedido dele com print, 15/10/2026;
                      a 1a versao abria dentro da celula, numa coluna so'). */}
                  {etqAberta === l.id && (
                    <tr style={{ borderBottom: `1px solid ${NAVY}` }}>
                      <td colSpan={6} style={{ paddingBottom: 6 }}>
                        <div className="border rounded-lg p-2" style={{ backgroundColor: 'var(--panel-bg)' }}>
                          <SeletorEtiquetas
                            etiquetas={etiquetasVisiveis.filter((e) => !e.nao_cliente || e.exibir_gestao || l.etiquetas.some((x) => x.id === e.id))}
                            marcadas={l.etiquetas.map((x) => x.id)}
                            onAlternar={(id) => void alternarEtiqueta(l, id)}
                            desabilitado={gravandoEtq === l.id}
                            avisoEmDuasColunas
                          />
                          <div className="mt-2 flex justify-end">
                            <button type="button" className="btn-primario text-sm px-3 py-1" onClick={() => setEtqAberta(null)}>
                              Fechar
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
